import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Play,
  Pause,
  Trash2,
  RefreshCw,
  Plus,
  Loader2,
  Bot,
  FlaskConical,
  CheckCircle2,
  XCircle,
  Clock,
  Calendar,
  MoreVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { evalApi } from '@/services/evalApi';
import { agentSchedulesApi, AgentSchedule } from '@/services/agentSchedulesApi';
import { CreateScheduleDialog } from '@/components/evals/CreateScheduleDialog';
import { CreateAgentScheduleDialog } from '@/components/schedules/CreateAgentScheduleDialog';

interface EvalSchedule {
  id: string;
  eval_id: string;
  eval_name?: string;
  cron_expression?: string;
  enabled: boolean;
  last_run_at?: string;
  next_run_at?: string;
  last_run_status?: 'passed' | 'failed' | 'running' | null;
}

export function SchedulesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'agents';

  // Eval schedules state
  const [evalSchedules, setEvalSchedules] = useState<EvalSchedule[]>([]);
  const [evalLoading, setEvalLoading] = useState(false);

  // Agent schedules state
  const [agentSchedules, setAgentSchedules] = useState<AgentSchedule[]>([]);
  const [agentLoading, setAgentLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [createEvalDialogOpen, setCreateEvalDialogOpen] = useState(false);
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<{ id: string; type: 'eval' | 'agent' } | null>(null);

  // Helper functions
  const formatCronExpression = (cron?: string, intervalMinutes?: number): string => {
    if (intervalMinutes) {
      if (intervalMinutes === 60) return 'Every hour';
      if (intervalMinutes === 1440) return 'Every day';
      if (intervalMinutes < 60) return `Every ${intervalMinutes} minutes`;
      return `Every ${Math.round(intervalMinutes / 60)} hours`;
    }
    if (!cron) return 'Not scheduled';

    // Simple cron parsing for common patterns
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      return `Daily at ${hour}:${minute.padStart(2, '0')}`;
    }
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '1-5') {
      return `Weekdays at ${hour}:${minute.padStart(2, '0')}`;
    }
    return cron;
  };

  const formatRelativeTime = (dateStr?: string): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(Math.abs(diffMs) / 60000);
    const diffHours = Math.floor(Math.abs(diffMs) / 3600000);
    const diffDays = Math.floor(Math.abs(diffMs) / 86400000);
    const isFuture = diffMs < 0;

    if (diffMins < 1) return isFuture ? 'In a moment' : 'Just now';
    if (diffMins < 60) return isFuture ? `In ${diffMins}m` : `${diffMins}m ago`;
    if (diffHours < 24) return isFuture ? `In ${diffHours}h` : `${diffHours}h ago`;
    if (diffDays < 7) return isFuture ? `In ${diffDays}d` : `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchEvalSchedules = async () => {
    try {
      setEvalLoading(true);
      setError(null);
      const data = await evalApi.listSchedules();
      setEvalSchedules(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load eval schedules');
    } finally {
      setEvalLoading(false);
    }
  };

  const fetchAgentSchedules = async () => {
    try {
      setAgentLoading(true);
      setError(null);
      const data = await agentSchedulesApi.listAgentSchedules();
      setAgentSchedules(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent schedules');
    } finally {
      setAgentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'evals') {
      fetchEvalSchedules();
    } else if (activeTab === 'agents') {
      fetchAgentSchedules();
    }
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const handleRefresh = () => {
    if (activeTab === 'evals') {
      fetchEvalSchedules();
    } else {
      fetchAgentSchedules();
    }
  };

  // Eval schedule handlers
  const handleEvalTrigger = async (scheduleId: string) => {
    try {
      await evalApi.triggerSchedule(scheduleId);
      fetchEvalSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger schedule');
    }
  };

  const handleEvalToggle = async (scheduleId: string, currentEnabled: boolean) => {
    try {
      await evalApi.updateSchedule(scheduleId, !currentEnabled);
      fetchEvalSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
    }
  };

  const confirmDelete = (id: string, type: 'eval' | 'agent') => {
    setScheduleToDelete({ id, type });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;
    try {
      if (scheduleToDelete.type === 'eval') {
        await evalApi.deleteSchedule(scheduleToDelete.id);
        fetchEvalSchedules();
      } else {
        await agentSchedulesApi.deleteAgentSchedule(scheduleToDelete.id);
        fetchAgentSchedules();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
    } finally {
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    }
  };

  // Agent schedule handlers
  const handleAgentTrigger = async (scheduleId: string) => {
    try {
      await agentSchedulesApi.triggerAgentSchedule(scheduleId);
      fetchAgentSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger schedule');
    }
  };

  const handleAgentToggle = async (scheduleId: string, currentEnabled: boolean) => {
    try {
      await agentSchedulesApi.updateAgentSchedule(scheduleId, !currentEnabled);
      fetchAgentSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
    }
  };

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Schedules</h1>
        <div className="flex gap-2">
          {activeTab === 'agents' && (
            <Button onClick={() => setCreateAgentDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          )}
          {activeTab === 'evals' && (
            <Button onClick={() => setCreateEvalDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="agents" className="gap-2">
            <Bot className="h-4 w-4" />
            Agent Runs
            {agentSchedules.length > 0 && (
              <Badge variant="secondary" className="ml-1">{agentSchedules.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="evals" className="gap-2">
            <FlaskConical className="h-4 w-4" />
            Eval Runs
            {evalSchedules.length > 0 && (
              <Badge variant="secondary" className="ml-1">{evalSchedules.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Agent Schedules */}
        <TabsContent value="agents">
          {agentLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : agentSchedules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  No agent schedules configured.
                </p>
                <Button onClick={() => setCreateAgentDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {agentSchedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      {/* Left side - Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Bot className="h-5 w-5 text-muted-foreground" />
                          <h3 className="font-semibold text-lg">
                            {schedule.agent_name || schedule.agent_id.substring(0, 8)}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatCronExpression(schedule.cron_expression, schedule.interval_minutes)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm mt-2">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            Last: {formatRelativeTime(schedule.last_run_at)}
                            {schedule.last_run_status === 'passed' && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 ml-1" />
                            )}
                            {schedule.last_run_status === 'failed' && (
                              <XCircle className="h-3.5 w-3.5 text-red-500 ml-1" />
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            Next: {formatRelativeTime(schedule.next_run_at)}
                          </span>
                        </div>
                      </div>

                      {/* Right side - Actions */}
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={schedule.enabled}
                          onCheckedChange={() => handleAgentToggle(schedule.id, schedule.enabled)}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleAgentTrigger(schedule.id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => confirmDelete(schedule.id, 'agent')}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Eval Schedules */}
        <TabsContent value="evals">
          {evalLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : evalSchedules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FlaskConical className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  No eval schedules configured.
                </p>
                <Button onClick={() => setCreateEvalDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {evalSchedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      {/* Left side - Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FlaskConical className="h-5 w-5 text-muted-foreground" />
                          <Button
                            variant="link"
                            className="p-0 h-auto text-lg font-semibold"
                            onClick={() => navigate(`/evals/${schedule.eval_id}`)}
                          >
                            {schedule.eval_name || schedule.eval_id.substring(0, 16)}
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatCronExpression(schedule.cron_expression)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm mt-2">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            Last: {formatRelativeTime(schedule.last_run_at)}
                            {schedule.last_run_status === 'passed' && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 ml-1" />
                            )}
                            {schedule.last_run_status === 'failed' && (
                              <XCircle className="h-3.5 w-3.5 text-red-500 ml-1" />
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            Next: {formatRelativeTime(schedule.next_run_at)}
                          </span>
                        </div>
                      </div>

                      {/* Right side - Actions */}
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={schedule.enabled}
                          onCheckedChange={() => handleEvalToggle(schedule.id, schedule.enabled)}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEvalTrigger(schedule.id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/evals/${schedule.eval_id}`)}>
                              <FlaskConical className="h-4 w-4 mr-2" />
                              View Eval
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => confirmDelete(schedule.id, 'eval')}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dialogs */}
      <CreateScheduleDialog
        open={createEvalDialogOpen}
        onClose={() => setCreateEvalDialogOpen(false)}
        onSuccess={fetchEvalSchedules}
      />

      <CreateAgentScheduleDialog
        open={createAgentDialogOpen}
        onClose={() => setCreateAgentDialogOpen(false)}
        onSuccess={fetchAgentSchedules}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this schedule? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setScheduleToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SchedulesPage;
